import type {
  AnyCoreEvent,
  AnyCoreEventHandler,
  CoreEventHandler,
  CoreEventName,
  CoreEvents,
} from "./events";

type Listeners = {
  [K in CoreEventName]?: Set<CoreEventHandler<K>>;
};

/**
 * Function returned by `on()` / `onAny()` to remove the listener. Idempotent — safe
 * to call from React effect cleanup or any other one-shot teardown path.
 */
export type Disposer = () => void;

export class Emitter {
  private listeners: Listeners = {};
  private anyListeners: Set<AnyCoreEventHandler> = new Set();

  /**
   * Subscribe to a specific event. Returns a disposer; calling it removes the
   * listener (equivalent to `off(event, handler)`). The disposer is the
   * preferred cleanup pattern — `off()` stays available for explicit handler
   * tracking.
   */
  on<K extends CoreEventName>(event: K, handler: CoreEventHandler<K>): Disposer {
    const set = (this.listeners[event] ?? new Set()) as Set<
      CoreEventHandler<K>
    >;
    set.add(handler);
    this.listeners[event] = set as Listeners[K];
    return () => this.off(event, handler);
  }

  off<K extends CoreEventName>(event: K, handler: CoreEventHandler<K>): void {
    const set = this.listeners[event] as Set<CoreEventHandler<K>> | undefined;
    set?.delete(handler);
  }

  /**
   * Subscribe to every event. Returns a disposer; calling it removes the listener
   * (equivalent to `offAny(handler)`).
   */
  onAny(handler: AnyCoreEventHandler): Disposer {
    this.anyListeners.add(handler);
    return () => this.offAny(handler);
  }

  offAny(handler: AnyCoreEventHandler): void {
    this.anyListeners.delete(handler);
  }

  emit<K extends CoreEventName>(event: K, payload: CoreEvents[K]): void {
    // Snapshot listener sets before iterating. A handler that subscribes another
    // handler during emit (e.g. registering a follow-up listener from inside a
    // one-shot disposer pattern) would otherwise receive the current event too
    // — JS Set iteration visits values inserted after iteration starts.
    const specific = this.listeners[event] as
      | Set<CoreEventHandler<K>>
      | undefined;
    if (specific && specific.size > 0) {
      for (const handler of Array.from(specific)) {
        handler(payload);
      }
    }
    if (this.anyListeners.size === 0) return;
    const anyEvent = { type: event, payload } as AnyCoreEvent;
    for (const handler of Array.from(this.anyListeners)) {
      handler(anyEvent);
    }
  }

  removeAllListeners(): void {
    this.listeners = {};
    this.anyListeners.clear();
  }
}
